import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import { QButton, QStack, useTheme } from '@captivateiq/quotient';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  DefaultTooltipContent,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { TooltipProps } from 'recharts/types/component/Tooltip';

import { useLineChart } from './hooks/useLineChart';
import { LineChartContainer } from './LineChartContainer.styles';

import { LineChartProps, SeriesChartStyle } from '../../types';
import {
  CHAR_WIDTH_PX,
  deriveBandName,
  formatDateString,
  formatLabel,
  getChartColors,
  isISODateString,
  X_AXIS_LABEL_ANGLE_ROTATED,
  X_AXIS_LABEL_ANGLE_STRAIGHT,
} from '../../utils/chartUtils';
import { ChartLegend } from '../BarChart/ChartLegend';
import { useAutoRotateLabels } from '../../hooks/useAutoRotateLabels';

// Tooltip marker prefix for range-band Area entries. Double-underscore avoids collision with any
// real column name. The handleTooltipHover formatter detects this prefix to render bands as
// concise "lower – upper" rows instead of raw [lo, hi] tuples.
const BAND_NAME_PREFIX = '__band_';
const bandName = (index: number): string => `${BAND_NAME_PREFIX}${index}`;

export const LineChart = ({
  targetLine,
  onClick,
  hideAxes,
  angleLabels = 'auto',
  showDots = 'auto',
  xAxisDateFormat,
  seriesStyles,
  rangeBands,
  ...chartProps
}: LineChartProps) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
  const [chartWidth, setChartWidth] = useState(0);

  // Use the auto-rotate labels hook for intelligent label rotation
  // Estimate yAxisWidth for initial calculation (typical Y-axis is 40-60px)
  const { containerRef, effectiveAngleLabels } = useAutoRotateLabels({
    data: chartProps.data,
    xAxisKeys: chartProps.xAxisKeys,
    yAxisWidth: 50, // Estimated Y-axis width; hook will recalculate on resize
    hideXAxis: hideAxes,
    hideYAxis: hideAxes,
    isHorizontal: false, // LineChart is always vertical
    angleLabels: angleLabels === 'auto' ? false : angleLabels,
    showFullLabels: false,
  });

  // Track chart width for dot density calculations
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setChartWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [containerRef]);

  /**
   * Format x-axis values with date handling
   * - If xAxisDateFormat is provided (user's locale formatter), use it
   * - Otherwise, auto-detect ISO dates and apply default 'short' format
   * - Fall back to formatLabel for non-dates
   */
  const formatXAxisValue = useCallback(
    (value: any): string => {
      // Use user's locale-based formatter if provided
      if (xAxisDateFormat) {
        return xAxisDateFormat(value);
      }

      // Auto-detect dates and apply default format
      if (isISODateString(value)) {
        return formatDateString(value, 'short');
      }

      // Fall back to standard label formatting
      return formatLabel(value);
    },
    [xAxisDateFormat],
  );

  /**
   * Determine label rotation based on angleLabels prop and hook calculation
   * - If explicitly set (true/false), use that value
   * - If 'auto', use the hook's calculated value
   */
  const shouldRotateLabels = angleLabels === 'auto' ? effectiveAngleLabels : angleLabels;

  /**
   * Calculate max label length for bottom margin calculations
   * Uses formatted label lengths for accurate height estimation when rotated
   */
  const maxLabelLength =
    chartProps.data.length > 0 && shouldRotateLabels
      ? Math.max(
          ...chartProps.data.map((datum) => {
            const value = datum[chartProps.xAxisKeys[0]];
            return formatXAxisValue(value).length;
          }),
        )
      : 0;

  const { data, formatValue, xAxisKeys, xAxisLabels, xAxisLabelSpacing, yAxisKeys, yAxisLabels, yAxisWidth } =
    useLineChart({ ...chartProps, angleLabels: shouldRotateLabels });

  // Sort bands by mean range width (descending) so the widest band sits behind. The originalIndex
  // is preserved for stable color/opacity assignment so reordering doesn't reshuffle colors.
  const sortedRangeBands = (rangeBands ?? [])
    .map((band, originalIndex) => {
      const { sum, count } = data.reduce(
        (acc, row) => {
          const lower = Number(row[band.lowerKey]);
          const upper = Number(row[band.upperKey]);
          if (Number.isFinite(lower) && Number.isFinite(upper)) {
            return { sum: acc.sum + Math.abs(upper - lower), count: acc.count + 1 };
          }
          return acc;
        },
        { sum: 0, count: 0 },
      );
      const meanWidth = count > 0 ? sum / count : 0;
      return { band, originalIndex, meanWidth };
    })
    .sort((a, b) => b.meanWidth - a.meanWidth);

  // Filter band_bound entries out of legend labels — they exist only to project data for bands
  // and have no visible series of their own. Pair each label with its color by original index so
  // colors stay stable regardless of which entries were filtered. Memoized so ChartLegend's
  // inputs and the band palette offset don't churn on every hover/resize render.
  const legendItems = useMemo(
    () =>
      yAxisKeys
        .map((key, index) => ({
          key,
          label: yAxisLabels[index],
          color: chartColors[index % chartColors.length].default,
        }))
        .filter(({ key }) => seriesStyles?.[key] !== 'band_bound'),
    [yAxisKeys, yAxisLabels, chartColors, seriesStyles],
  );

  /**
   * Calculate bottom margin for x-axis labels
   * Adds extra space for rotated labels based on their length
   */
  const calculateBottomMargin = () => {
    if (hideAxes) {
      return 10;
    }

    if (!shouldRotateLabels) {
      return xAxisLabelSpacing(xAxisKeys[0]);
    }

    // When rotated at 45°, labels extend vertically by: width * sin(45°)
    // Add some base padding for the axis line and tick marks
    const rotatedLabelHeight = Math.ceil(maxLabelLength * CHAR_WIDTH_PX * Math.sin(Math.PI / 4));
    const basePadding = 5; // Space for axis line and tick marks

    return rotatedLabelHeight + basePadding;
  };

  /**
   * Calculate whether to show dots and their size based on data density
   */
  const calculateDotSettings = () => {
    // Explicit control overrides auto calculation
    if (showDots === false) {
      return { show: false, radius: 0, activeRadius: 0 };
    }
    if (showDots === true) {
      return { show: true, radius: 4, activeRadius: 6 };
    }

    // Auto mode: calculate based on data density
    if (chartWidth === 0 || data.length === 0) {
      return { show: true, radius: 4, activeRadius: 6 };
    }

    const pixelsPerPoint = chartWidth / data.length;

    // Thresholds for dot behavior
    const HIDE_DOTS_THRESHOLD = 3; // Hide when less than 3px per point
    const MIN_DOT_SIZE_THRESHOLD = 8; // Start shrinking when less than 8px per point
    const MAX_DOT_RADIUS = 4;
    const MIN_DOT_RADIUS = 2;

    if (pixelsPerPoint < HIDE_DOTS_THRESHOLD) {
      // Too dense - hide dots but keep active dot for interaction
      return { show: false, radius: 0, activeRadius: 6 };
    }

    if (pixelsPerPoint < MIN_DOT_SIZE_THRESHOLD) {
      // Moderately dense - scale down dot size
      const scale = (pixelsPerPoint - HIDE_DOTS_THRESHOLD) / (MIN_DOT_SIZE_THRESHOLD - HIDE_DOTS_THRESHOLD);
      const radius = MIN_DOT_RADIUS + (MAX_DOT_RADIUS - MIN_DOT_RADIUS) * scale;
      return {
        show: true,
        radius: Math.floor(radius),
        activeRadius: 6,
      };
    }

    // Sparse data - show full-size dots
    return { show: true, radius: MAX_DOT_RADIUS, activeRadius: 6 };
  };

  const dotSettings = calculateDotSettings();

  // Lookup table for resolving band data keys back to friendly labels in the tooltip. yAxisKeys
  // and yAxisLabels are positionally paired and include band_bound entries (they're filtered
  // out of rendering but still flow through useLineChart). Memoized so handleTooltipHover's
  // useCallback identity stays stable across renders that don't change keys/labels.
  const dataKeyToLabel = useMemo<Record<string, string>>(() => {
    const lookup: Record<string, string> = {};
    yAxisKeys.forEach((key, index) => {
      if (yAxisLabels[index]) lookup[key] = yAxisLabels[index];
    });
    return lookup;
  }, [yAxisKeys, yAxisLabels]);

  /**
   * Recharts tooltip handler. Range-band entries (name starts with `__band_`) collapse the
   * shared prefix between the lower and upper column labels — e.g. for "Model prediction
   * (Amount) — 95% lower (Sum)" and "...— 95% upper (Sum)", the band is named "Model prediction
   * (Amount) — 95%". Falls back to "Range" / "Range N" if the labels don't share a meaningful
   * word-aligned prefix. Regular line/area entries fall through to DefaultTooltipContent.
   */
  const handleTooltipHover = useCallback(
    ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
      if (active && payload?.length) {
        const xAxisLabel = xAxisLabels && xAxisLabels.length > 0 ? xAxisLabels[0] : 'Value';

        let displayLabel = label;
        if (hideAxes && payload[0]?.payload) {
          const actualValue = payload[0].payload[xAxisKeys[0]];
          if (actualValue !== undefined) {
            displayLabel = actualValue;
          }
        }

        const formattedLabel = `${xAxisLabel}: ${formatXAxisValue(displayLabel)}`;
        const bandCount = rangeBands?.length ?? 0;

        const reformattedPayload = payload.map((item: any) => {
          const name = typeof item.name === 'string' ? item.name : '';
          if (name.startsWith(BAND_NAME_PREFIX) && rangeBands) {
            const bandIndex = Number(name.slice(BAND_NAME_PREFIX.length));
            const band = rangeBands[bandIndex];
            if (band && item.payload) {
              const lower = formatValue(item.payload[band.lowerKey]);
              const upper = formatValue(item.payload[band.upperKey]);
              const derivedName = deriveBandName(
                dataKeyToLabel[band.lowerKey],
                dataKeyToLabel[band.upperKey],
                bandIndex,
                bandCount,
              );
              return { ...item, name: derivedName, value: `${lower} – ${upper}` };
            }
          }
          return { ...item, value: formatValue(item.value) };
        });

        return <DefaultTooltipContent label={formattedLabel} payload={reformattedPayload} />;
      }
      return null;
    },
    [formatValue, xAxisLabels, hideAxes, xAxisKeys, formatXAxisValue, rangeBands, dataKeyToLabel],
  );

  return (
    <LineChartContainer data-testid="line-chart" ref={containerRef}>
      {legendItems.length > 1 && (
        <QStack alignItems="flex-start" direction="row" spacing={1} sx={{ mb: 4 }}>
          <QButton
            color="primary"
            data-testid="view-legend-button"
            size="small"
            startIcon={faList}
            variant="text"
            onClick={() => setIsLegendModalOpen(true)}
          >
            View legend
          </QButton>
        </QStack>
      )}
      <ResponsiveContainer key={yAxisKeys.length}>
        <ComposedChart
          data={data}
          margin={{
            bottom: calculateBottomMargin(),
            right: hideAxes ? 10 : 50,
            top: hideAxes ? 10 : 0,
            left: hideAxes ? 10 : 0,
          }}
          onClick={onClick}
        >
          {!hideAxes && <CartesianGrid stroke={theme.palette.neutral.primary[100]} strokeDasharray="3 3" />}
          {!hideAxes && (
            <XAxis
              angle={shouldRotateLabels ? X_AXIS_LABEL_ANGLE_ROTATED : X_AXIS_LABEL_ANGLE_STRAIGHT}
              dataKey={xAxisKeys[0]}
              padding={{ left: 10, right: 50 }}
              stroke={theme.palette.neutral.primary[700]}
              tick={{ fill: theme.palette.neutral.primary[700], textAnchor: 'start' }}
              tickFormatter={formatXAxisValue}
              tickLine={false}
            />
          )}
          {!hideAxes && (
            <YAxis
              minTickGap={0.5}
              padding={{ top: 10 }}
              stroke={theme.palette.neutral.primary[700]}
              tick={{ fill: theme.palette.neutral.primary[700], textAnchor: 'end' }}
              tickFormatter={formatValue}
              tickLine={false}
              width={yAxisWidth}
            />
          )}
          <Tooltip content={handleTooltipHover} />

          {/* Render range bands BEFORE lines/areas so they sit behind. Bands are auto-sorted
              by mean range width (largest first → deepest), so a wider band drawn second can't
              fully cover a narrower band drawn first, regardless of the order the user added
              them in. Each band gets a stroke at its own color so its lower/upper bounds read
              as paired edges, with fill opacity tied to the original add-order (the first
              band the user added stays the most saturated). */}
          {sortedRangeBands.map(({ band, originalIndex }) => {
            // Offset the band palette past the visible series so the first band doesn't share
            // its color with the first line. legendItems is already the visible series count.
            const palette = chartColors[(legendItems.length + originalIndex) % chartColors.length];
            const color = band.color ?? palette.default;
            const fillOpacity = Math.max(0.18, 0.45 - originalIndex * 0.12);
            return (
              <Area
                dataKey={(row: Record<string, any>) => [row[band.lowerKey], row[band.upperKey]]}
                fill={color}
                fillOpacity={fillOpacity}
                isAnimationActive={false}
                key={`band-${originalIndex}-${band.lowerKey}-${band.upperKey}`}
                legendType="none"
                name={bandName(originalIndex)}
                stroke={color}
                strokeOpacity={0.85}
                strokeWidth={1.5}
              />
            );
          })}

          {/* Render line / area series. Fields styled `band_bound` exist only so their column
              data is projected for range bands — they are not rendered as their own series. */}
          {yAxisKeys?.map((dataKey, seriesIndex) => {
            const style: SeriesChartStyle = seriesStyles?.[dataKey] ?? 'line';
            if (style === 'band_bound') {
              return null;
            }
            const colors = chartColors[seriesIndex % chartColors.length];

            let dotConfig;
            if (hideAxes) {
              dotConfig = false;
            } else if (dotSettings.show) {
              dotConfig = { fill: colors.default, strokeWidth: 2, r: dotSettings.radius };
            } else {
              dotConfig = false;
            }

            let activeDotConfig;
            if (hideAxes) {
              activeDotConfig = false;
            } else if (dotSettings.activeRadius > 0) {
              activeDotConfig = { r: dotSettings.activeRadius, strokeWidth: 1, fill: colors.hover };
            } else {
              activeDotConfig = false;
            }

            if (style === 'area') {
              return (
                <Area
                  activeDot={activeDotConfig}
                  dataKey={dataKey}
                  dot={dotConfig}
                  fill={colors.default}
                  fillOpacity={0.4}
                  key={dataKey}
                  name={yAxisLabels[seriesIndex]}
                  stroke={colors.default}
                  strokeWidth={2}
                />
              );
            }

            return (
              <Line
                activeDot={activeDotConfig}
                dataKey={dataKey}
                dot={dotConfig}
                key={dataKey}
                name={yAxisLabels[seriesIndex]}
                stroke={colors.default}
                strokeWidth={2}
              />
            );
          })}

          {/* Render target line */}
          {targetLine !== undefined && targetLine !== null && (
            <>
              <ReferenceLine
                ifOverflow="extendDomain"
                isFront
                stroke={theme.palette.common.white}
                strokeLinecap="round"
                strokeWidth={4}
                y={targetLine}
              />
              <ReferenceLine
                ifOverflow="extendDomain"
                isFront
                stroke={theme.palette.denotive.positive[600]}
                strokeLinecap="round"
                strokeWidth={2}
                y={targetLine}
              />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
      <ChartLegend
        chartColors={legendItems.map(({ color }) => ({ default: color, hover: color }))}
        labels={legendItems.map(({ label }) => label)}
        open={isLegendModalOpen}
        onClose={() => setIsLegendModalOpen(false)}
      />
    </LineChartContainer>
  );
};
